/* Peerless practice-test quiz logic. Shared by sections/practice-test.liquid and the
   preview harness. Vanilla JS, no dependencies.

   DOM CONTRACT (set by snippets/quiz-content.liquid + the section/harness shell):
     [data-ptq]                  quiz container (one per page)
       .ptq-quiz[data-name][data-pass][data-total]
       [data-mats]               study-materials panel, above the questions
         [data-promo]            discount block inside it, ships hidden
           [data-promo-copy][data-promo-value] [data-promo-label]
       [data-q][data-answer][data-topic][data-explanation]  each question
         input[type=radio]       choices, value = choice index
       [data-progress] [data-progress-fill] [data-progress-label]
       [data-form]               the <form> wrapping the questions
       [data-incomplete]         "answer all N" note
       [data-see-results]        button to advance to the gate
       [data-gate] [data-gate-form] [data-gate-email] [data-gate-phone]
         [data-gate-test-timing] [data-gate-error]
       [data-results] [data-score] [data-score-total] [data-verdict]
         [data-weak-wrap] [data-weak] [data-review] [data-materials]

   Klaviyo wired: subscribe (/client/subscriptions) + Completed Quiz event (/client/events). */

(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // US phone: 10 digits, optional leading country code "1". Area/exchange
  // codes can't start with 0 or 1 (NANP rules).
  var PHONE_RE = /^1?[2-9]\d{2}[2-9]\d{6}$/;

  // Ad attribution for lead capture. `gclid` is the join key that later lets
  // Google Ads learn which leads actually bought (offline conversion import);
  // unlike everything else on the lead it cannot be reconstructed after the fact,
  // so it is stashed the first time it is seen and reused if the visitor reaches
  // the quiz on a later page view.
  //
  // First touch wins: once a paid/campaign click is recorded it is never
  // overwritten, otherwise a later organic visit would erase the ad that earned
  // the lead. LIMITATION: this only runs on practice-test pages, so an ad click
  // landing elsewhere and navigating here is not captured — closing that gap
  // means moving this into the theme layout so it runs site-wide.
  var ATTR_KEY = 'ptq_attr';
  var ATTR_FIELDS = ['gclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

  function readAttribution() {
    var stored = {};
    try { stored = JSON.parse(localStorage.getItem(ATTR_KEY) || '{}') || {}; } catch (e) { stored = {}; }
    if (stored.gclid || stored.wbraid || stored.gbraid || stored.utm_source) return stored;

    var attr = {
      landing_page: String(window.location.href).slice(0, 500),
      referrer: String(document.referrer || '').slice(0, 500)
    };
    try {
      var params = new URLSearchParams(window.location.search);
      ATTR_FIELDS.forEach(function (f) {
        var v = params.get(f);
        if (v) attr[f] = String(v).slice(0, 200);
      });
    } catch (e) { /* older browsers: fall through with page-level attribution only */ }

    try { localStorage.setItem(ATTR_KEY, JSON.stringify(attr)); } catch (e) { /* private mode */ }
    return attr;
  }

  function digits(s) {
    return (s || '').replace(/\D/g, '');
  }

  // GA4 on this store runs inside Shopify's Web Pixel sandbox, so window.gtag /
  // window.dataLayer are NOT reachable from theme JS. Shopify.analytics.publish()
  // is the supported bridge: a Custom Pixel (Settings → Customer events) subscribes
  // to these event names and forwards them to GA4 via its own gtag.
  function trackQuizEvent(eventName, payload) {
    try {
      if (window.Shopify && window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, payload);
      }
    } catch (err) {
      console.warn('[Peerless quiz] analytics publish failed:', err);
    }
  }

  function init(root) {
    if (!root || root.__ptqInit) return;
    var quiz = root.querySelector('.ptq-quiz');
    if (!quiz) return;
    root.__ptqInit = true;
    // Generation counter — stale event listeners from previous inits bail out early.
    var gen = (root.__ptqGen = (root.__ptqGen || 0) + 1);

    var total = parseInt(quiz.getAttribute('data-total'), 10) || 0;
    var pass = parseInt(quiz.getAttribute('data-pass'), 10) || Math.ceil(total * 0.7);
    var examName = quiz.getAttribute('data-name') || '';
    var examKey = quiz.getAttribute('data-exam') || '';

    // Track: Quiz viewed
    trackQuizEvent('ptq_quiz_viewed', {
      exam_name: examName,
      exam_slug: examKey
    });

    var questions = Array.prototype.slice.call(root.querySelectorAll('[data-q]'));
    var fill = root.querySelector('[data-progress-fill]');
    var label = root.querySelector('[data-progress-label]');
    var incomplete = root.querySelector('[data-incomplete]');
    var seeBtn = root.querySelector('[data-see-results]');
    var gate = root.querySelector('[data-gate]');
    var gateForm = root.querySelector('[data-gate-form]');
    var results = root.querySelector('[data-results]');


    function answeredCount() {
      return questions.filter(function (q) {
        return q.querySelector('input[type=radio]:checked');
      }).length;
    }

    function updateProgress() {
      var n = answeredCount();
      if (fill) fill.style.width = total ? (n / total) * 100 + '%' : '0%';
      if (label) label.textContent = n + ' of ' + total + ' answered';
      if (incomplete && n === total) incomplete.hidden = true;
    }

    root.addEventListener('change', function (e) {
      if (e.target && e.target.matches('input[type=radio]')) updateProgress();
    });

    function firstUnanswered() {
      for (var i = 0; i < questions.length; i++) {
        if (!questions[i].querySelector('input[type=radio]:checked')) return questions[i];
      }
      return null;
    }

    if (seeBtn) {
      seeBtn.addEventListener('click', function () {
        if (answeredCount() < total) {
          if (incomplete) incomplete.hidden = false;
          var miss = firstUnanswered();
          if (miss) miss.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        if (incomplete) incomplete.hidden = true;
        seeBtn.hidden = true;
        if (gate) {
          gate.hidden = false;
          gate.scrollIntoView({ behavior: 'smooth', block: 'start' });
          var email = gate.querySelector('[data-gate-email]');
          if (email) email.focus();
          // Track: Gate shown (all questions answered)
          trackQuizEvent('ptq_gate_shown', {
            exam_name: examName,
            exam_slug: examKey
          });
        }
      });
    }

    if (gateForm) {
      gateForm.addEventListener('submit', function (e) {
        e.preventDefault();
        if (root.__ptqGen !== gen) return; // stale listener from a previous init
        var emailEl = gateForm.querySelector('[data-gate-email]');
        var phoneEl = gateForm.querySelector('[data-gate-phone]');
        var testTimingEl = gateForm.querySelector('[data-gate-test-timing]');
        var errEl = gateForm.querySelector('[data-gate-error]');
        var email = emailEl ? emailEl.value.trim() : '';
        var phone = phoneEl ? phoneEl.value.trim() : '';
        // Optional and never validated into a blocking error — see the comment on this
        // field in practice-test.liquid. Empty means "not answered", which the server
        // turns into "leave the metafield alone".
        var testTiming = testTimingEl ? testTimingEl.value : '';
        // The exam whose quiz they just sat, sent straight through. This used to be a
        // text input prefilled with exactly this value; the field is gone, the answer is
        // not, so segments built on custom.which_test_are_you_taking keep filling.
        var whichTest = examName;
        // Consent is given by submitting the form, disclosed in the sentence above the
        // button. There is no phone number to text when the optional field is left
        // blank, so SMS permission follows the number rather than the submission.
        var smsConsent = Boolean(phone);

        function fail(msg) {
          if (errEl) { errEl.textContent = msg; errEl.hidden = false; }
        }

        if (!EMAIL_RE.test(email)) return fail('Please enter a valid email address.');
        // Phone stays optional — results are never withheld for not leaving a number.
        // A number that IS given still has to be a real one, or the SMS channel gets
        // created against garbage.
        if (phone && !PHONE_RE.test(digits(phone))) {
          return fail('Please enter a valid 10-digit mobile phone number, or leave it blank.');
        }
        if (errEl) errEl.hidden = true;

        var graded = grade();

        /* === LEAD CAPTURE =================================================== */
        // POSTs to the Peerless middleware, which writes the lead to BOTH the
        // Shopify customer list (so Shopify Messaging and Flow can reach it) and
        // the shared Neon `leads` table (retargeting + Google Ads offline
        // conversion import).
        //
        // This replaces a direct browser->Omnisend call that had two problems:
        // it wrote ONLY to Omnisend, leaving 17 of 26 quiz leads absent from
        // Shopify entirely, and it shipped a read/write API key in page source.
        // Omnisend has since been retired.
        //
        // The server hop is not optional. Capturing straight into Shopify from
        // the browser fails — POSTing form_type=customer to /contact returns
        //     400 {"errors":"Missing CAPTCHA token"}
        // because Shopify's spam protection guards customer forms and a scripted
        // fetch cannot supply a token. The Admin API call has to happen
        // server-side, which is exactly what this endpoint does.
        //
        // Fire-and-forget with keepalive: results render immediately and the
        // request still completes if the visitor navigates away. A capture
        // failure must never block someone from seeing the score they earned.
        var attr = readAttribution();
        fetch('https://peerless-middleware.vercel.app/api/quiz-lead', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          keepalive: true,
          body: JSON.stringify({
            email: email,
            phone: phone,
            smsConsent: smsConsent,
            examKey: examKey,
            examName: examName,
            score: graded.score,
            total: total,
            gclid: attr.gclid || null,
            utmSource: attr.utm_source || null,
            utmMedium: attr.utm_medium || null,
            utmCampaign: attr.utm_campaign || null,
            utmTerm: attr.utm_term || null,
            utmContent: attr.utm_content || null,
            landingPage: attr.landing_page || null,
            referrer: attr.referrer || null,
            testTiming: testTiming || null,
            whichTest: whichTest || null
          })
        }).catch(function (err) { console.warn('[quiz-lead] capture failed:', err); });

        var passed = graded.score >= pass;
        // Track: Quiz completed with GA4 funnel events
        trackQuizEvent('ptq_quiz_completed', {
          exam_name: examName,
          exam_slug: examKey,
          score: graded.score,
          total: total,
          passed: passed
        });
        // Track: Quiz passed (if applicable)
        if (passed) {
          trackQuizEvent('ptq_quiz_passed', {
            exam_name: examName,
            exam_slug: examKey,
            score: graded.score,
            total: total
          });
        }
        /* ===================================================================== */

        if (gate) gate.hidden = true;
        renderResults(graded);
      });
    }

    function grade() {
      var score = 0;
      var weak = [];
      var detail = questions.map(function (q, i) {
        var correct = parseInt(q.getAttribute('data-answer'), 10);
        var checked = q.querySelector('input[type=radio]:checked');
        var chosen = checked ? parseInt(checked.value, 10) : -1;
        var ok = chosen === correct;
        if (ok) score++; else {
          var topic = q.getAttribute('data-topic') || '';
          if (topic && weak.indexOf(topic) === -1) weak.push(topic);
        }
        var labels = Array.prototype.map.call(
          q.querySelectorAll('.ptq-choice__txt'),
          function (s) { return s.textContent; }
        );
        var prompt = q.querySelector('.ptq-q__prompt');
        return {
          n: i + 1,
          ok: ok,
          prompt: prompt ? prompt.textContent.replace(/^\s*\d+\s*/, '') : '',
          chosen: chosen > -1 ? labels[chosen] : '(no answer)',
          correct: labels[correct],
          explanation: q.getAttribute('data-explanation') || ''
        };
      });
      return { score: score, weakTopics: weak, detail: detail };
    }

    function renderResults(graded) {
      var passed = graded.score >= pass;

      // Replace the sticky progress bar with an inline scorecard
      var progressEl = root.querySelector('[data-progress]');
      if (progressEl) {
        var sc = document.createElement('div');
        sc.className = 'ptq-results__scorecard';
        sc.innerHTML =
          '<div class="ptq-results__score"><span data-score>' + graded.score + '</span>' +
          '<span class="ptq-results__score-total" data-score-total>/' + total + '</span></div>' +
          '<div class="ptq-results__verdict" data-verdict data-pass="' + String(passed) + '">' +
          (passed
            ? 'Nice — you’re on track. Tighten up the misses below and you’re ready.'
            : 'Not quite passing yet. Here is exactly what to study before exam day.') +
          '</div>';
        progressEl.replaceWith(sc);
      }

      // Weak-topics strip (only if there are misses)
      var weakSection = null;
      if (graded.weakTopics.length > 0) {
        weakSection = document.createElement('div');
        weakSection.className = 'ptq-results__section';
        var wh = document.createElement('h3');
        wh.className = 'ptq-results__h';
        wh.textContent = 'Topics to study';
        var wul = document.createElement('ul');
        wul.className = 'ptq-weak';
        graded.weakTopics.forEach(function (t) {
          var li = document.createElement('li');
          li.textContent = t;
          wul.appendChild(li);
        });
        weakSection.appendChild(wh);
        weakSection.appendChild(wul);
      }

      // Build the "Your answers" review list
      var reviewSection = document.createElement('div');
      reviewSection.className = 'ptq-results__section';
      var reviewH = document.createElement('h3');
      reviewH.className = 'ptq-results__h';
      reviewH.textContent = 'Your answers';
      var reviewOl = document.createElement('ol');
      reviewOl.className = 'ptq-review';
      graded.detail.forEach(function (d) {
        var li = document.createElement('li');
        li.className = 'ptq-review__item';
        var icon = document.createElement('span');
        icon.className = 'ptq-review__icon ' + (d.ok ? 'ptq-review__icon--ok' : 'ptq-review__icon--no');
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = d.ok ? '✓' : '✗';
        var body = document.createElement('div');
        body.className = 'ptq-review__body';
        var qp = document.createElement('p');
        qp.className = 'ptq-review__q';
        qp.textContent = d.n + '. ' + d.prompt;
        body.appendChild(qp);
        if (!d.ok) {
          var your = document.createElement('p');
          your.className = 'ptq-review__a';
          your.textContent = 'Your answer: ' + d.chosen;
          body.appendChild(your);
        }
        var corr = document.createElement('p');
        corr.className = 'ptq-review__a';
        corr.innerHTML = 'Correct answer: <b></b>';
        corr.querySelector('b').textContent = d.correct;
        body.appendChild(corr);
        if (d.explanation) {
          var exp = document.createElement('p');
          exp.className = 'ptq-review__exp';
          exp.textContent = d.explanation;
          body.appendChild(exp);
        }
        li.appendChild(icon);
        li.appendChild(body);
        reviewOl.appendChild(li);
      });
      reviewSection.appendChild(reviewH);
      reviewSection.appendChild(reviewOl);

      // Replace the question form with sections in order:
      // [Topics to study] → [Your answers]. Study materials are NOT moved here — there
      // is one materials block and it already sits at the top of the page, above the
      // scorecard, where the promo below reveals itself.
      var form = root.querySelector('[data-form]');
      if (form) {
        var parent = form.parentNode;
        if (weakSection) parent.insertBefore(weakSection, form);
        parent.replaceChild(reviewSection, form);
      }

      // Hide the now-redundant results panel, see-results button, and incomplete message
      if (seeBtn) seeBtn.hidden = true;
      if (incomplete) incomplete.hidden = true;
      if (results) results.hidden = true;

      // Reveal the discount, then scroll back up to the materials panel so the offer and
      // the products it applies to are the first thing on screen. Finishing the quiz is
      // the only thing that unhides this — it is the reward for completing, not a
      // banner every visitor sees. The scorecard sits immediately below.
      var promo = root.querySelector('[data-promo]');
      var mats = root.querySelector('[data-mats]');
      if (promo) promo.hidden = false;

      var scrollTarget = mats || root.querySelector('.ptq-results__scorecard');
      if (scrollTarget) scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Copy-to-clipboard for the discount code. writeText is unavailable on non-HTTPS
    // origins and in older Safari, so the code stays visible as plain text and the
    // button falls back to selecting it — a failed copy must never hide the code.
    var promoBtn = root.querySelector('[data-promo-copy]');
    if (promoBtn) {
      promoBtn.addEventListener('click', function () {
        var code = promoBtn.getAttribute('data-promo-value') || '';
        var labelEl = promoBtn.querySelector('[data-promo-label]');

        function confirmCopied() {
          promoBtn.setAttribute('data-copied', 'true');
          if (labelEl) labelEl.textContent = 'Copied';
          setTimeout(function () {
            promoBtn.removeAttribute('data-copied');
            if (labelEl) labelEl.textContent = 'Copy code';
          }, 2500);
        }

        function selectCode() {
          var txt = promoBtn.querySelector('.ptq-promo__code-txt');
          if (!txt || !window.getSelection || !document.createRange) return;
          var range = document.createRange();
          range.selectNodeContents(txt);
          var sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
          if (labelEl) labelEl.textContent = 'Copy it';
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(confirmCopied, selectCode);
        } else {
          selectCode();
        }
      });
    }

    updateProgress();
  }

  function autoInit() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-ptq]'), init);
  }

  window.PeerlessQuiz = { init: init, autoInit: autoInit };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
})();
